/**
 * Recording preferences — which note profile the next recording uses.
 *
 * Two separate ideas, deliberately (CLA-594):
 *
 *   defaultProfileId  — the clinician's baseline, stored on their ACCOUNT
 *                       (`recording_profile_id`, settings cascade). Changed
 *                       only from Settings → Note profiles. Follows them to
 *                       every device.
 *
 *   overrideProfileId — "this recording only". Set from the record bar,
 *                       in-memory, never persisted, cleared once the recording
 *                       is submitted.
 *
 * Keeping them separate is the point. Originally there was one sticky value:
 * choosing a profile for a single consult silently became the baseline until
 * you remembered to change it back. Making that value account-wide (the first
 * half of CLA-594) fixed a clinician recording with no profile at all on a
 * second browser — but it also meant a one-off choice then followed her to
 * every device.
 *
 * The clinician is the one who knows whether this consult is an initial or a
 * follow-up — with no bookings there is nothing to infer it from — so the
 * per-consult switch has to be cheap AND free of consequences beyond the
 * consult.
 *
 * `effectiveProfileId()` is what recording entry points read: the override if
 * one is set, otherwise the account default.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { api } from '@/api/client'

interface RecordingPrefsState {
  /** Account default. null = auto-detect. Local mirror of the server value. */
  defaultProfileId: number | null
  /** This-recording-only choice. null = follow the default. Not persisted. */
  overrideProfileId: number | null

  /** What the next recording should actually use. */
  effectiveProfileId: () => number | null
  /** True when this recording deviates from the account default. */
  isOverridden: () => boolean

  /** Change the account default (Settings). Persists to the account. */
  setDefaultProfileId: (id: number | null) => Promise<void>
  /** Choose a profile for this recording only (record bar). Local, cheap. */
  setOverrideProfileId: (id: number | null) => void
  /** Drop back to the account default. Called once a recording is submitted. */
  clearOverride: () => void
  /** Adopt the server's default. Called when settings load. Never writes back. */
  hydrate: (id: number | null) => void
}

export const useRecordingPrefs = create<RecordingPrefsState>()(
  persist(
    (set, get) => ({
      defaultProfileId: null,
      overrideProfileId: null,

      effectiveProfileId: () => {
        const { overrideProfileId, defaultProfileId } = get()
        return overrideProfileId !== null ? overrideProfileId : defaultProfileId
      },

      isOverridden: () => {
        const { overrideProfileId, defaultProfileId } = get()
        return overrideProfileId !== null && overrideProfileId !== defaultProfileId
      },

      setDefaultProfileId: async (id) => {
        const previous = get().defaultProfileId
        // A new baseline supersedes any one-off choice still in play —
        // otherwise the setting they just changed appears not to take effect.
        set({ defaultProfileId: id, overrideProfileId: null })

        try {
          await api.post('/settings/save', {
            key: 'recording_profile_id',
            value: id === null ? 'auto' : String(id),
          })
        } catch (e) {
          // Keep the local choice — the next recording should still honour what
          // they picked — but don't pretend it reached the account.
          console.error('Failed to save default note profile', e)
          if (previous !== id) throw e
        }
      },

      setOverrideProfileId: (id) => set({ overrideProfileId: id }),

      clearOverride: () => {
        if (get().overrideProfileId !== null) set({ overrideProfileId: null })
      },

      hydrate: (id) => {
        if (get().defaultProfileId !== id) set({ defaultProfileId: id })
      },
    }),
    {
      name: 'claro.recordingPrefs.v1',
      storage: createJSONStorage(() => localStorage),
      // Only the default is cached locally, and only so the record button is
      // synchronous on a cold load before settings arrive — the account is the
      // source of truth. The override is per-recording state and must never
      // survive a reload, or it becomes the sticky value all over again.
      partialize: (s) => ({ defaultProfileId: s.defaultProfileId }) as RecordingPrefsState,
    }
  )
)
